export const GUIDANCE_TEXT =
  "This app is for guidance purposes only. Always consult your medical team.";

export const v2Copy = {
  en: {
    brand: "Dialysis Diet Pro",
    guidance: GUIDANCE_TEXT,
    login: "Log In",
    register: "Register",
    guest: "Continue as Guest",
    dashboard: "Dashboard",
    foods: "Foods",
    addCustomFood: "Add Custom Food",
    foodLogging: "Log Food",
    history: "History",
    mixer: "Mixer",
    reminders: "Reminders",
    settings: "Settings",
    assistant: "Assistant",
    admin: "Admin",
    logout: "Log out",
    moduleStatus: "Overview",
    notBuilt: "Use this section to manage your dialysis diet tools.",
    manualFirst: "Food logging is manual-first and only confirms after the app completes the save.",
    noAiSave: "AI food logging is disabled. AI cannot save food, write food logs, or claim a food action completed.",
    dashboardNotes:
      "Review daily nutrient cards, safety summary, approved tips, and quote of the day from your logged data.",
    foodNotes:
      "Browse admin foods and your custom foods together in one searchable food list.",
    customFoodNotes:
      "Create foods with quantity, unit, potassium, phosphorus, sodium, protein, category, and optional Arabic name.",
    loggingNotes:
      "Search or select a food, choose quantity, preview nutrient impact, then save through the app.",
    historyNotes:
      "Review nutrient snapshots from your food logs so older records stay stable.",
    mixerNotes:
      "Combine foods or manual nutrient numbers, then save the result as a custom food.",
    remindersNotes:
      "Manage medication reminders and the daily evening reminder here.",
    adminNotes:
      "Manage users, foods, approved tips, quotes, knowledge base, app settings, and audit logs.",
    assistantNotes:
      "The assistant is read-only. Food logging through the assistant is currently disabled.",
    guestNotes:
      "Use Dialysis Diet Pro without creating an account. You can switch to an account later when sync is available.",
    continueGuest: "Continue as Guest",
    guestStorageNotice: "Guest data is stored on this device only.",
    guestMode: "Guest mode",
    registeredMode: "Signed in",
    profileSession: "Account session",
    authNotes: "Access your dashboard, food tools, history, reminders, and settings.",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    passwordMismatch: "Passwords do not match.",
    forgotPassword: "Forgot password?",
    continueToDashboard: "Log In",
    fullName: "Full name",
    weight: "Weight",
    profile: "Profile",
    preferences: "Preferences",
    notifications: "Notifications",
    language: "Language",
    theme: "Theme",
    light: "Light",
    dark: "Dark",
    system: "System",
    reminderSettings: "Notifications",
    enableReminders: "Enable reminders",
    enableNotifications: "Enable notifications",
    whatsappSettings: "WhatsApp Reminders",
    whatsappSaveOnly: "WhatsApp reminders are settings-only. No WhatsApp messages are sent from this page.",
    whatsappNumber: "WhatsApp number",
    enableWhatsappReminders: "Enable WhatsApp reminders",
    reminderChannel: "Reminder channel preference",
    appNotification: "App notification",
    whatsapp: "WhatsApp",
    both: "Both",
    dailyLimits: "Daily Limits",
    dailyLimitsSetup: "Set your daily limits with your care team.",
    potassium: "Potassium",
    phosphorus: "Phosphorus",
    sodium: "Sodium",
    protein: "Protein",
    weightFluidCard: "Show optional weight/fluid card",
    settingsNotes:
      "Manage your profile, daily limits, language, theme, notifications, and reminder preferences.",
    saveSettings: "Save settings",
    settingsSaved: "Settings saved.",
    forgotTitle: "Reset your password",
    forgotNotes: "Enter your email and we will help you continue with password recovery.",
    sendResetLink: "Send reset link",
    forgotSentTitle: "Check your inbox",
    forgotSentNotes: "If an account exists for that email, a reset link will be sent shortly.",
    backToLogin: "Back to Log In",
    dashboardCopy: {
      title: "Dashboard",
      description: "Track daily limits, nutrient progress, and next actions for {name}.",
      todayDate: "Today",
      consumed: "Consumed",
      limit: "Limit",
      remaining: "Remaining",
      percentageUsed: "used",
      notSet: "Not set",
      loading: "Loading dashboard data...",
      dataErrorTitle: "Dashboard data could not be loaded.",
      dataErrorBody: "Refresh the page or try again from this device.",
      noFoodToday: "No food logged today.",
      noPeriodLogs: "No food logged for this period yet.",
      noTargets: "Daily targets are not configured.",
      setTargets: "Set daily targets with your renal dietitian or medical team.",
      safetySummaryTitle: "Today's Safety Summary",
      safetySummaryDescription: "Based on today's tracked entries and configured targets.",
      quickActionsTitle: "Quick Actions",
      addFood: "Add Food",
      addCustomFood: "Add Custom Food",
      openMixer: "Open Mixer",
      viewHistory: "View History",
      reminders: "Reminders",
      settings: "Settings",
      dailyProgressTitle: "Daily Progress",
      dailyProgressDescription: "Review local food-log totals by period.",
      today: "Today",
      yesterday: "Yesterday",
      last7: "Last 7 days",
      totalNutrients: "Total nutrients",
      entries: "entries",
      averageLimitUse: "Average limit use",
      weightFluid: "Weight / Fluid",
      currentWeight: "Current weight",
      targetWeight: "Target weight",
      fluidLimit: "Fluid limit",
      approvedTips: "Approved Tips",
      noApprovedTips: "No approved dashboard tips are available.",
      quoteOfTheDay: "Quote of the Day",
      noQuote: "No approved quote is available today.",
      footerSafety: "For tracking purposes only. Confirm personal targets with your renal dietitian or medical team.",
      nutrients: {
        potassium: "Potassium",
        phosphorus: "Phosphorus",
        sodium: "Sodium",
        protein: "Protein",
      },
      statuses: {
        safe: "Within range",
        watch: "Getting close",
        over: "Over limit",
        notConfigured: "Target not configured",
      },
      safety: {
        noLogsTitle: "No food logged today.",
        noLogsDetail:
          "Based on today's tracked entries, no nutrient totals are available yet. Add food logs to review your configured ranges.",
        noTargetsTitle: "Targets are not configured.",
        noTargetsDetail: "Set daily targets with your renal dietitian or medical team before using the range summary.",
        safeTitle: "Within the configured tracking range.",
        safeDetail:
          "Based on today's tracked entries, all configured nutrient totals are below 70% of their daily limits.",
        watchTitle: "{nutrients} {verb} getting close.",
        watchDetail: "Based on today's tracked entries, one or more nutrients are getting close to the configured daily limit.",
        overTitle: "{nutrients} {verb} over the configured limit.",
        overDetail: "Based on today's tracked entries, review the over-limit nutrients and confirm personal targets with your medical team.",
        missingTargetsDetail: "Some targets are not configured, so the summary is limited to configured nutrients.",
      },
      periods: {
        today: "Today",
        yesterday: "Yesterday",
        last7: "Last 7 days",
      },
      listAnd: "and",
    },
    foodsCopy: {
      title: "Foods",
      description: "Browse verified foods and your custom foods in one searchable food list. Food icons are for display only.",
      addCustomFood: "Add Custom Food",
      searchPlaceholder: "Search by name or category",
      all: "All",
      verified: "Verified",
      custom: "Custom",
      allCategories: "All categories",
      loading: "Loading foods...",
      noFoodsTitle: "No foods found",
      noFoodsDescription: "Adjust your search or add a custom food with measured nutrient values.",
      deleteConfirm: "Delete {name}?",
      deleteError: "Custom food could not be deleted.",
      deleteSuccess: "Custom food deleted.",
      uncategorized: "Uncategorized",
      serving: "Serving",
      source: "Source",
      localFoodList: "Local food list",
      logFood: "Log Food",
      delete: "Delete",
      foodLabels: {
        verified: "Verified Food",
        custom: "Custom Food",
        food: "Food",
      },
      addTitle: "Add Custom Food",
      addDescription: "Create a food with measured serving details and nutrient values for your local food list.",
      backToFoods: "Back to Foods",
      foodDetails: "Food Details",
      foodName: "Food name",
      arabicName: "Arabic name",
      category: "Category",
      categoryPlaceholder: "Custom",
      icon: "Icon",
      servingTitle: "Serving",
      quantity: "Quantity",
      quantityUnit: "Quantity unit",
      nutrientsTitle: "Nutrients",
      potassiumMg: "Potassium (mg)",
      phosphorusMg: "Phosphorus (mg)",
      sodiumMg: "Sodium (mg)",
      proteinG: "Protein (g)",
      cancel: "Cancel",
      saveCustomFood: "Save Custom Food",
      saveError: "Check the custom food details.",
      saveSuccess: "Custom food saved.",
      iconOptions: {
        custom: "Custom",
        fruit: "Fruit",
        vegetable: "Vegetable",
        protein: "Protein",
        grain: "Grain",
        dairy: "Dairy",
        meal: "Meal",
      },
    },
    foodLoggingCopy: {
      title: "Log Food",
      description: "Select a food, enter portions eaten, review the nutrient snapshot, then save it to your history.",
      backToFoods: "Back to Foods",
      selectFood: "Select Food",
      searchPlaceholder: "Search foods",
      selected: "Selected",
      portionShort: "portion(s)",
      saveFoodLog: "Save Food Log",
      noMatchesTitle: "No foods match these filters.",
      noMatchesDescription: "Try another category, change your search, or add a custom food.",
      openMixer: "Open Mixer",
      selectedFood: "Selected Food",
      selectedFoodEmpty: "Choose a food to review serving details.",
      portionsEaten: "Portions Eaten",
      portionsLabel: "Portions eaten",
      portionsPlaceholder: "Examples: 0.5, 1, 2, 3",
      onePortionEquals: "1 portion equals the listed serving: {serving}.",
      reviewBeforeSaving: "Review Before Saving",
      snapshotNotice: "These nutrient snapshots will be stored with this food log.",
      previewEmpty: "Select a food and enter portions greater than 0 to preview nutrient totals.",
      listedServing: "Listed serving",
      selectFoodError: "Select a food first.",
      quantityError: "Check the portions before saving.",
      saveError: "Food could not be logged.",
      saveSuccess: "Food logged successfully.",
      loadingFoods: "Loading foods...",
    },
    historyCopy: {
      title: "History",
      description: "Review food logs with nutrient snapshots saved at the time each item was recorded.",
      selectedDayPdf: "Selected Day PDF",
      fourteenDayPdf: "14-Day PDF",
      thirtyDayPdf: "30-Day PDF",
      logFood: "Log Food",
      selectDate: "Select Date",
      chooseDay: "Choose a day to review.",
      todaysLogs: "Today's Logs",
      todayCount: "{count} item(s) recorded today.",
      daysWithHistory: "Days With History",
      noFoodLoggedYet: "No food logged yet.",
      dailyTotals: "Daily Totals",
      foodItems: "Food Items",
      showingSnapshots: "Showing nutrient snapshots for {date}.",
      noFoodForDate: "No food logged for this date.",
      deleteConfirm: "Delete {name}?",
      deleteError: "Food log could not be deleted.",
      deleteSuccess: "Food log deleted.",
      portionsEaten: "Portions eaten",
      listedServing: "Listed serving",
      delete: "Delete",
      loading: "Loading history...",
      searchPlaceholder: "Filter food items",
      dataError: "Food history could not be loaded.",
    },
  },
  ar: {
    brand: "Dialysis Diet Pro",
    guidance: "هذا التطبيق لأغراض إرشادية فقط. استشر دائمًا فريقك الطبي.",
    login: "تسجيل الدخول",
    register: "إنشاء حساب",
    guest: "المتابعة كضيف",
    dashboard: "لوحة التحكم",
    foods: "الأطعمة",
    addCustomFood: "إضافة طعام مخصص",
    foodLogging: "تسجيل الطعام",
    history: "السجل",
    mixer: "الحاسبة",
    reminders: "التذكيرات",
    settings: "الإعدادات",
    assistant: "المساعد",
    admin: "الإدارة",
    logout: "تسجيل الخروج",
    moduleStatus: "نظرة عامة",
    notBuilt: "استخدم هذا القسم لإدارة أدوات حمية غسيل الكلى.",
    manualFirst: "تسجيل الطعام يدوي أولًا ولا يتم التأكيد إلا بعد أن يكمل التطبيق الحفظ.",
    noAiSave: "تسجيل الطعام عبر الذكاء الاصطناعي معطل. لا يستطيع الذكاء الاصطناعي حفظ الطعام أو كتابة سجلات الطعام.",
    dashboardNotes:
      "راجع بطاقات العناصر اليومية وملخص السلامة والنصائح والاقتباس اليومي من بياناتك المسجلة.",
    foodNotes:
      "تصفح أطعمة النظام والأطعمة المخصصة الخاصة بك معًا في قائمة واحدة قابلة للبحث.",
    customFoodNotes:
      "أنشئ أطعمة مع الكمية والوحدة والبوتاسيوم والفوسفور والصوديوم والبروتين والفئة والاسم العربي الاختياري.",
    loggingNotes:
      "ابحث عن الطعام أو حدده، اختر الكمية، راجع تأثير العناصر، ثم احفظ من داخل التطبيق.",
    historyNotes:
      "راجع لقطات العناصر الغذائية من سجلات الطعام حتى تبقى السجلات القديمة مستقرة.",
    mixerNotes:
      "اجمع الأطعمة أو أدخل أرقام العناصر يدويًا، ثم احفظ النتيجة كطعام مخصص.",
    remindersNotes:
      "أدر تذكيرات الدواء والتذكير المسائي اليومي هنا.",
    adminNotes:
      "إدارة المستخدمين والأطعمة والنصائح والاقتباسات وقاعدة المعرفة وإعدادات التطبيق وسجلات التدقيق.",
    assistantNotes:
      "المساعد للقراءة فقط. تسجيل الطعام من خلال المساعد معطل حاليًا.",
    guestNotes:
      "استخدم Dialysis Diet Pro دون إنشاء حساب. يمكنك الانتقال إلى حساب لاحقًا عندما يتوفر المزامنة.",
    continueGuest: "المتابعة كضيف",
    guestStorageNotice: "بيانات الضيف محفوظة على هذا الجهاز فقط.",
    guestMode: "وضع الضيف",
    registeredMode: "تم تسجيل الدخول",
    profileSession: "جلسة الحساب",
    authNotes: "ادخل إلى لوحة التحكم وأدوات الطعام والسجل والتذكيرات والإعدادات.",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    confirmPassword: "تأكيد كلمة المرور",
    passwordMismatch: "كلمتا المرور غير متطابقتين.",
    forgotPassword: "هل نسيت كلمة المرور؟",
    continueToDashboard: "تسجيل الدخول",
    fullName: "الاسم الكامل",
    weight: "الوزن",
    profile: "الملف الشخصي",
    preferences: "التفضيلات",
    notifications: "الإشعارات",
    language: "اللغة",
    theme: "المظهر",
    light: "فاتح",
    dark: "داكن",
    system: "النظام",
    reminderSettings: "الإشعارات",
    enableReminders: "تفعيل التذكيرات",
    enableNotifications: "تفعيل الإشعارات",
    whatsappSettings: "تذكيرات واتساب",
    whatsappSaveOnly: "تذكيرات واتساب محفوظة كإعدادات فقط. لا يتم إرسال رسائل واتساب من هذه الصفحة.",
    whatsappNumber: "رقم واتساب",
    enableWhatsappReminders: "تفعيل تذكيرات واتساب",
    reminderChannel: "تفضيل قناة التذكير",
    appNotification: "إشعار داخل التطبيق",
    whatsapp: "واتساب",
    both: "كلاهما",
    dailyLimits: "الحدود اليومية",
    dailyLimitsSetup: "Set your daily limits with your care team.",
    potassium: "البوتاسيوم",
    phosphorus: "الفوسفور",
    sodium: "الصوديوم",
    protein: "البروتين",
    weightFluidCard: "إظهار بطاقة الوزن/السوائل الاختيارية",
    settingsNotes:
      "إدارة ملفك الشخصي والحدود اليومية واللغة والمظهر والإشعارات وتفضيلات التذكير.",
    saveSettings: "حفظ الإعدادات",
    settingsSaved: "تم حفظ الإعدادات.",
    forgotTitle: "إعادة تعيين كلمة المرور",
    forgotNotes: "أدخل بريدك الإلكتروني وسنساعدك على متابعة استعادة كلمة المرور.",
    sendResetLink: "إرسال رابط إعادة التعيين",
    forgotSentTitle: "تحقق من بريدك",
    forgotSentNotes: "إذا كان هناك حساب لهذا البريد، سيتم إرسال رابط إعادة التعيين قريبًا.",
    backToLogin: "العودة إلى تسجيل الدخول",
    dashboardCopy: {
      title: "لوحة التحكم",
      description: "تابع الحدود اليومية وتقدم العناصر والخطوات التالية لـ {name}.",
      todayDate: "اليوم",
      consumed: "المستهلك",
      limit: "الحد",
      remaining: "المتبقي",
      percentageUsed: "مستخدم",
      notSet: "غير محدد",
      loading: "جار تحميل بيانات لوحة التحكم...",
      dataErrorTitle: "تعذر تحميل بيانات لوحة التحكم.",
      dataErrorBody: "حدّث الصفحة أو حاول مرة أخرى من هذا الجهاز.",
      noFoodToday: "لم يتم تسجيل طعام اليوم.",
      noPeriodLogs: "لم يتم تسجيل طعام لهذه الفترة بعد.",
      noTargets: "لم يتم ضبط الأهداف اليومية.",
      setTargets: "اضبط الأهداف اليومية مع اختصاصي التغذية الكلوية أو فريقك الطبي.",
      safetySummaryTitle: "ملخص تتبع اليوم",
      safetySummaryDescription: "استنادًا إلى إدخالات اليوم المتتبعة والأهداف المضبوطة.",
      quickActionsTitle: "إجراءات سريعة",
      addFood: "تسجيل طعام",
      addCustomFood: "إضافة طعام مخصص",
      openMixer: "فتح الحاسبة",
      viewHistory: "عرض السجل",
      reminders: "التذكيرات",
      settings: "الإعدادات",
      dailyProgressTitle: "التقدم اليومي",
      dailyProgressDescription: "راجع إجماليات سجل الطعام المحلي حسب الفترة.",
      today: "اليوم",
      yesterday: "أمس",
      last7: "آخر 7 أيام",
      totalNutrients: "إجمالي العناصر",
      entries: "إدخالات",
      averageLimitUse: "متوسط استخدام الحدود",
      weightFluid: "الوزن / السوائل",
      currentWeight: "الوزن الحالي",
      targetWeight: "الوزن المستهدف",
      fluidLimit: "حد السوائل",
      approvedTips: "نصائح معتمدة",
      noApprovedTips: "لا توجد نصائح معتمدة للوحة التحكم.",
      quoteOfTheDay: "اقتباس اليوم",
      noQuote: "لا يوجد اقتباس معتمد اليوم.",
      footerSafety: "لأغراض التتبع فقط. أكّد أهدافك الشخصية مع اختصاصي التغذية الكلوية أو فريقك الطبي.",
      nutrients: {
        potassium: "البوتاسيوم",
        phosphorus: "الفوسفور",
        sodium: "الصوديوم",
        protein: "البروتين",
      },
      statuses: {
        safe: "ضمن النطاق",
        watch: "يقترب من الحد",
        over: "فوق الحد",
        notConfigured: "الهدف غير مضبوط",
      },
      safety: {
        noLogsTitle: "لم يتم تسجيل طعام اليوم.",
        noLogsDetail:
          "استنادًا إلى إدخالات اليوم المتتبعة، لا توجد إجماليات عناصر بعد. أضف سجلات طعام لمراجعة النطاقات المضبوطة.",
        noTargetsTitle: "الأهداف غير مضبوطة.",
        noTargetsDetail: "اضبط الأهداف اليومية مع اختصاصي التغذية الكلوية أو فريقك الطبي قبل استخدام ملخص النطاق.",
        safeTitle: "ضمن نطاق التتبع المضبوط.",
        safeDetail:
          "استنادًا إلى إدخالات اليوم المتتبعة، كل إجماليات العناصر المضبوطة أقل من 70% من حدودها اليومية.",
        watchTitle: "{nutrients} يقترب من الحد.",
        watchDetail: "استنادًا إلى إدخالات اليوم المتتبعة، عنصر واحد أو أكثر يقترب من الحد اليومي المضبوط.",
        overTitle: "{nutrients} فوق الحد المضبوط.",
        overDetail: "استنادًا إلى إدخالات اليوم المتتبعة، راجع العناصر فوق الحد وأكّد أهدافك الشخصية مع فريقك الطبي.",
        missingTargetsDetail: "بعض الأهداف غير مضبوطة، لذلك يقتصر الملخص على العناصر ذات الأهداف المضبوطة.",
      },
      periods: {
        today: "اليوم",
        yesterday: "أمس",
        last7: "آخر 7 أيام",
      },
      listAnd: "و",
    },
    foodsCopy: {
      title: "الأطعمة",
      description: "تصفح الأطعمة المعتمدة وأطعمتك المخصصة في قائمة واحدة قابلة للبحث. الأيقونات للعرض فقط.",
      addCustomFood: "إضافة طعام مخصص",
      searchPlaceholder: "ابحث حسب الاسم أو الفئة",
      all: "الكل",
      verified: "معتمد",
      custom: "مخصص",
      allCategories: "كل الفئات",
      loading: "جارٍ تحميل الأطعمة...",
      noFoodsTitle: "لم يتم العثور على أطعمة",
      noFoodsDescription: "عدّل البحث أو أضف طعامًا مخصصًا بقيم عناصر مقاسة.",
      deleteConfirm: "حذف {name}؟",
      deleteError: "تعذر حذف الطعام المخصص.",
      deleteSuccess: "تم حذف الطعام المخصص.",
      uncategorized: "غير مصنف",
      serving: "الحصة",
      source: "المصدر",
      localFoodList: "قائمة الأطعمة المحلية",
      logFood: "تسجيل الطعام",
      delete: "حذف",
      foodLabels: {
        verified: "طعام معتمد",
        custom: "طعام مخصص",
        food: "طعام",
      },
      addTitle: "إضافة طعام مخصص",
      addDescription: "أنشئ طعامًا بتفاصيل حصة وقيم عناصر مقاسة لقائمة طعامك المحلية.",
      backToFoods: "العودة إلى الأطعمة",
      foodDetails: "تفاصيل الطعام",
      foodName: "اسم الطعام",
      arabicName: "الاسم العربي",
      category: "الفئة",
      categoryPlaceholder: "مخصص",
      icon: "الأيقونة",
      servingTitle: "الحصة",
      quantity: "الكمية",
      quantityUnit: "وحدة الكمية",
      nutrientsTitle: "العناصر",
      potassiumMg: "البوتاسيوم (mg)",
      phosphorusMg: "الفوسفور (mg)",
      sodiumMg: "الصوديوم (mg)",
      proteinG: "البروتين (g)",
      cancel: "إلغاء",
      saveCustomFood: "حفظ الطعام المخصص",
      saveError: "تحقق من تفاصيل الطعام المخصص.",
      saveSuccess: "تم حفظ الطعام المخصص.",
      iconOptions: {
        custom: "مخصص",
        fruit: "فاكهة",
        vegetable: "خضار",
        protein: "بروتين",
        grain: "حبوب",
        dairy: "ألبان",
        meal: "وجبة",
      },
    },
    foodLoggingCopy: {
      title: "تسجيل الطعام",
      description: "حدد طعامًا، أدخل الحصص التي تناولتها، راجع لقطة العناصر، ثم احفظها في السجل.",
      backToFoods: "العودة إلى الأطعمة",
      selectFood: "اختيار الطعام",
      searchPlaceholder: "ابحث عن الأطعمة",
      selected: "المحدد",
      portionShort: "حصة",
      saveFoodLog: "حفظ سجل الطعام",
      noMatchesTitle: "لا توجد أطعمة تطابق هذه المرشحات.",
      noMatchesDescription: "جرّب فئة أخرى، غيّر البحث، أو أضف طعامًا مخصصًا.",
      openMixer: "فتح الحاسبة",
      selectedFood: "الطعام المحدد",
      selectedFoodEmpty: "اختر طعامًا لمراجعة تفاصيل الحصة.",
      portionsEaten: "الحصص المتناولة",
      portionsLabel: "الحصص المتناولة",
      portionsPlaceholder: "أمثلة: 0.5، 1، 2، 3",
      onePortionEquals: "حصة واحدة تساوي الحصة المدرجة: {serving}.",
      reviewBeforeSaving: "المراجعة قبل الحفظ",
      snapshotNotice: "سيتم حفظ لقطات العناصر هذه مع سجل الطعام.",
      previewEmpty: "اختر طعامًا وأدخل حصصًا أكبر من 0 لمعاينة مجموع العناصر.",
      listedServing: "الحصة المدرجة",
      selectFoodError: "اختر طعامًا أولًا.",
      quantityError: "تحقق من الحصص قبل الحفظ.",
      saveError: "تعذر تسجيل الطعام.",
      saveSuccess: "تم تسجيل الطعام بنجاح.",
      loadingFoods: "جارٍ تحميل الأطعمة...",
    },
    historyCopy: {
      title: "السجل",
      description: "راجع سجلات الطعام مع لقطات العناصر المحفوظة وقت تسجيل كل عنصر.",
      selectedDayPdf: "PDF لليوم المحدد",
      fourteenDayPdf: "PDF لـ 14 يومًا",
      thirtyDayPdf: "PDF لـ 30 يومًا",
      logFood: "تسجيل الطعام",
      selectDate: "اختيار التاريخ",
      chooseDay: "اختر يومًا للمراجعة.",
      todaysLogs: "سجلات اليوم",
      todayCount: "{count} عنصر مسجل اليوم.",
      daysWithHistory: "أيام فيها سجل",
      noFoodLoggedYet: "لم يتم تسجيل طعام بعد.",
      dailyTotals: "مجموع اليوم",
      foodItems: "عناصر الطعام",
      showingSnapshots: "عرض لقطات العناصر ليوم {date}.",
      noFoodForDate: "لا يوجد طعام مسجل لهذا التاريخ.",
      deleteConfirm: "حذف {name}؟",
      deleteError: "تعذر حذف سجل الطعام.",
      deleteSuccess: "تم حذف سجل الطعام.",
      portionsEaten: "الحصص المتناولة",
      listedServing: "الحصة المدرجة",
      delete: "حذف",
      loading: "جارٍ تحميل السجل...",
      searchPlaceholder: "تصفية عناصر الطعام",
      dataError: "تعذر تحميل سجل الطعام.",
    },
  },
};

export function getV2Copy(lang) {
  return v2Copy[lang] || v2Copy.en;
}
